import React, { useState, useEffect } from 'react';
import { Grid, Box, Button, Typography, Stack } from '@mui/material';
import CateringForm from '../Catering/CateringForm';
import bg from '../../../assets/location header.png';
import { environment } from '../../../environments/environment';
import AboutFoodSection from '../../components/common/AboutFoodSection';
import foodImage from '../../../assets/burger.jpg';
import OrangeCircle from './OrangeCircle';
import FAQAccordion from './FAQAccordion';
import {
  getStoreStatus,
  getTodaysClosingTime,
  formatTimingForDay,
  ensureAmPmFormat,
} from '../../Utils/storeTimingUtils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { firestore, auth } from '../../../../../../libs/firebase-service/src';
import noItems from '../../../assets/Hamburger-pana 1.png';
import chickenBurger from '../../../assets/maxBurger.jpg';
import SchedulingDialog from '../../components/SchedulingDialog/SchedulingDialog';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, UpdateScheduledTime } from '@tabletuck/redux-slices';
const LocationDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { locationId: id } = useParams();
  const [storeData, setStoreData] = useState(location.state?.store || null);

  const [loading, setLoading] = useState(!location.state?.store);
  const [error, setError] = useState(null);
  const [showSchedulingDialog, setShowSchedulingDialog] = useState(false);

  const userData = useSelector((state) => state.authReducer.userData);
  // Fetch store data if not provided through navigation state
  useEffect(() => {
    const fetchStore = async () => {
      if (storeData || !id) return;

      try {
        setLoading(true);
        const storeDoc = await firestore.collection('stores').doc(id).get();

        if (storeDoc.exists) {
          const fetchedStore = { id: storeDoc.id, ...storeDoc.data() };
          setStoreData(fetchedStore);
        } else {
          setError('Store not found');
        }
      } catch (err) {
        console.error('Error fetching store:', err);
        setError('Failed to load store data');
      } finally {
        setLoading(false);
      }
    };

    fetchStore();
  }, [id, storeData]);

  // Helpers to normalize data model differences
  const getDayShorts = () => ['Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed'];

  // Use shared timing utilities so logic matches branch cards
  const storeStatus = getStoreStatus(storeData);
  const closingTime = getTodaysClosingTime(storeData);

  // Handle scheduling confirmation
  const handleSchedulingConfirm = (schedulingData) => {
    if (!storeData) return;

    // Store scheduling data in Redux with storeId to track which branch it's for
    dispatch(
      UpdateScheduledTime({
        ...schedulingData,
        storeId: storeData.id,
      })
    );

    // Determine order type based on store capabilities
    const orderType = storeData.delivery_enabled ? 'delivery' : 'pickup';

    // Update user preferences
    const updatedUser = {
      ...(userData || {}),
      pickupOrDelivery: orderType,
      defaultStore: storeData.id,
    };
    dispatch(setUser({ userData: updatedUser }));

    if (auth?.currentUser?.uid) {
      const uid = auth.currentUser.uid;
      firestore
        .collection('users')
        .doc(uid)
        .set(
          {
            pickupOrDelivery: orderType,
            defaultStore: storeData.id,
          },
          { merge: true }
        )
        .catch((e) => console.warn('Failed to persist after scheduling:', e));
    }

    // Close dialog
    setShowSchedulingDialog(false);

    // Navigate to menu
    navigate(`/menu/store/${storeData.id}`, {
      state: { store: storeData },
    });
  };

  // Handle scheduling dialog close
  const handleSchedulingClose = () => {
    setShowSchedulingDialog(false);
  };

  // Handle order now button click — go to menu and open branch/scheduling dialog
  const handleOrderNowClick = () => {
    navigate('/menu', { state: { openBranchDialog: true } });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '90vh',
        }}
      >
        <Typography>Loading store details...</Typography>
      </Box>
    );
  }

  if (error || !storeData) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: '90vh',
        }}
      >
        <img
          src={noItems}
          alt="Not found"
          style={{
            maxWidth: '363px',
            width: '100%',
            maxHeight: 'auto',
            marginBottom: '20px',
            objectFit: 'cover',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            marginBottom: '8px',
            color: '#172B4D',
            fontSize: {
              xs: '14px',
              md: '16px',
              lg: '20px',
            },
            fontFamily: 'Jost, sans-serif',
            textAlign: 'center',
          }}
        >
          {error || 'Store not found'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        fontFamily: '"Jost", sans-serif',
      }}
    >
      <Grid
        container
        direction="column"
        sx={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
          gap: { xs: 4, sm: 8, md: 12, lg: 20 },
          pb: { xs: 4, sm: 10 },
        }}
      >
        {/* Background Image */}
        <Grid
          item
          xs={12}
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: '300px', sm: '483px' },
            backgroundImage: `url(${bg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Black overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 28, sm: 36, lg: '36px' },
                  letterSpacing: 1,
                  mb: 1,
                  fontFamily: 'Oswald, sans-serif',
                  textTransform: 'uppercase',
                }}
              >
                {storeData?.businessName || storeData?.name || 'FATTY PATTY'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: 14, sm: '16px' },
                  mb: 2,
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    color: storeStatus.isOpen ? '#4CAF50' : '#F44336',
                  }}
                >
                  {storeStatus.isOpen ? 'Open' : 'Closed'}
                </span>
                {storeStatus.nextOpenTime &&
                  ` - Opens at ${storeStatus.nextOpenTime}`}
              </Typography>
              <Box>
                <button
                  style={{
                    background: environment?.mainColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: '24px',
                    padding: '8px 24px',
                    fontWeight: 700,
                    fontSize: '13px',
                    fontFamily: 'Jost, sans-serif',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    textTransform: 'uppercase',
                  }}
                  onClick={handleOrderNowClick}
                >
                  order now
                </button>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* White Overlay Container */}
        <Grid
          item
          xs={12}
          sx={{
            position: 'relative',
            width: '80%',
            mx: 'auto',
            mt: { xs: '-100px', sm: '-150px', md: '-250px' },
            backgroundColor: 'white',
            borderColor: 'divider',
            borderRadius: '16px',
            overflow: 'hidden',
            padding: '20px',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 3, md: 5 },
            fontFamily: '"Jost", sans-serif',
            boxShadow: '0px 4px 100px 0px #00000014',
            zIndex: 2,
          }}
        >
          {/* Map and Get Direction Button */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: { xs: '250px', md: '100%' },
              borderRadius: '12px',
              overflow: 'hidden',
              mb: { xs: 2, md: 0 },
            }}
          >
            <iframe
              title="location-map"
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0, borderRadius: '12px' }}
              src={`https://www.google.com/maps?q=${
                storeData?._geoloc?.lat || storeData?.address?.lat || 36.3882822
              },${
                storeData?._geoloc?.lng ||
                storeData?.address?.lng ||
                -86.4486959
              }&z=16&output=embed`}
              allowFullScreen=""
              aria-hidden="false"
              tabIndex="0"
            ></iframe>
            <Box sx={{ position: 'absolute', bottom: 16, right: 56 }}>
              <button
                style={{
                  background: '#FF5A1F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '24px',
                  padding: '8px 20px',
                  fontWeight: 700,
                  fontSize: 14,
                  fontFamily: 'Jost, sans-serif',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
                }}
                onClick={() => {
                  const lat =
                    storeData?._geoloc?.lat ||
                    storeData?.address?.lat ||
                    36.3882822;
                  const lng =
                    storeData?._geoloc?.lng ||
                    storeData?.address?.lng ||
                    -86.4486959;
                  window.open(
                    `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
                    '_blank'
                  );
                }}
              >
                Get Direction
              </button>
            </Box>
          </Box>
          {/* Data Section */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header Row: shown on sm+; on xs we show a header above each list so headings accompany each table when stacked */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'grid' },
                gridTemplateColumns: { sm: '1fr 1fr' },
                gap: { sm: 3, md: 5 },
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: { sm: 16, md: 18 },
                  color: '#172B4D',
                  fontFamily: 'Oswald, sans-serif',
                  textTransform: 'uppercase',
                  textAlign: 'left',
                  pb: 1,
                }}
              >
                Hours
              </Box>
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: { sm: 16, md: 18 },
                  color: '#172B4D',
                  fontFamily: 'Oswald, sans-serif',
                  textTransform: 'uppercase',
                  textAlign: 'left',
                  pb: 1,
                }}
              >
                Delivery Hours
              </Box>
            </Box>
            {/* Single horizontal line under both headers */}
            <Box
              sx={{
                width: '100%',
                height: '1px',
                background: '#E0E0E0',
                mb: { xs: 1, sm: 1.5 },
              }}
            />
            {/* Data Row with vertical divider */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1px 1fr' },
                gap: { xs: 3, sm: 2, md: 5 },
              }}
            >
              <Box>
                {/* Column header for small screens (visible only on xs) */}
                <Box
                  sx={{
                    display: { xs: 'block', sm: 'none' },
                    fontWeight: 700,
                    fontSize: 18,
                    color: '#172B4D',
                    fontFamily: 'Oswald, sans-serif',
                    textTransform: 'uppercase',
                    textAlign: 'left',
                    pb: 1,
                  }}
                >
                  Hours
                </Box>
                {['Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed'].map(
                  (dayShort, i) => {
                    const timeString =
                      formatTimingForDay(storeData, dayShort, false) ||
                      'Closed';

                    const fullDayName = [
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday',
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                    ][i];

                    return (
                      <Box
                        key={dayShort}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: { xs: 1, sm: 0.75, md: 1 },
                          fontSize: { xs: 15, sm: 13, md: 15 },
                          fontFamily: 'Jost, sans-serif',
                          gap: { sm: 1, md: 2 },
                        }}
                      >
                        <span
                          style={{ fontWeight: 500, minWidth: 'fit-content' }}
                        >
                          {fullDayName}
                        </span>
                        <span
                          style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                        >
                          {timeString}
                        </span>
                      </Box>
                    );
                  }
                )}
              </Box>
              {/* Vertical divider line */}
              <Box
                sx={{
                  width: '1px',
                  backgroundColor: '#E0E0E0',
                  display: { xs: 'none', sm: 'block' },
                  justifySelf: 'center',
                }}
              />
              <Box>
                {/* Column header for small screens (visible only on xs) */}
                <Box
                  sx={{
                    display: { xs: 'block', sm: 'none' },
                    fontWeight: 700,
                    fontSize: 18,
                    color: '#172B4D',
                    fontFamily: 'Oswald, sans-serif',
                    textTransform: 'uppercase',
                    textAlign: 'left',
                    pb: 1,
                  }}
                >
                  Delivery Hours
                </Box>
                {['Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed'].map(
                  (dayShort, i) => {
                    // Prefer deliveryTiming on the store if available
                    const timeString =
                      formatTimingForDay(storeData, dayShort, true) || 'Closed';

                    const fullDayName = [
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday',
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                    ][i];

                    return (
                      <Box
                        key={dayShort}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: { xs: 1, sm: 0.75, md: 1 },
                          fontSize: { xs: 15, sm: 13, md: 15 },
                          fontFamily: 'Jost, sans-serif',
                          gap: { sm: 1, md: 2 },
                        }}
                      >
                        <span
                          style={{ fontWeight: 500, minWidth: 'fit-content' }}
                        >
                          {fullDayName}
                        </span>
                        <span
                          style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                        >
                          {timeString}
                        </span>
                      </Box>
                    );
                  }
                )}
              </Box>
            </Box>
            <Button
              sx={{
                background: '#FD56221F',
                borderRadius: '8px',

                display: 'flex',
                alignItems: 'center',
                marginTop: 1,
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z"
                  fill="#FF5A1F"
                />
              </svg>
              <span
                style={{
                  color: environment?.mainColor,
                  fontWeight: 500,
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '14px',
                  marginLeft: 1,
                  display: 'inline-block',
                }}
              >
                Contact:
              </span>
              <span
                style={{
                  color: '#172B4D',
                  fontWeight: 700,
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '14px',
                  marginLeft: 1,
                  display: 'inline-block',
                }}
              >
                {storeData?.contact_number_1 || storeData?.contact_number_2}
              </span>
            </Button>

            {/* Address Information */}
            <Box
              variant="text"
              sx={{
                display: 'flex',

                textAlign: 'left',
                justifyContent: 'flex-start',
                width: '100%',
                p: 0,
                mt: 2,
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                  fill="#FF5A1F"
                />
              </svg>
              <span
                style={{
                  color: environment?.mainColor,
                  fontWeight: 500,
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '14px',
                  marginLeft: 8,
                  display: 'inline-block',
                }}
              >
                Address:
              </span>
              <span
                style={{
                  color: '#172B4D',
                  fontWeight: 700,
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '14px',
                  marginLeft: 8,
                  display: 'inline-block',

                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'wrap',
                  maxWidth: '400px',
                }}
              >
                {(() => {
                  const addr = storeData?.address;
                  if (!addr) return 'Address not available';

                  // Build US format address: addressLine1, addressLine2 (if exists), City, State ZipCode
                  const parts = [];

                  if (addr.addressLine1) parts.push(addr.addressLine1);
                  if (addr.addressLine2) parts.push(addr.addressLine2);

                  // City, State ZIP format
                  const cityStateParts = [];
                  if (addr.locality || addr.city) {
                    cityStateParts.push(addr.locality || addr.city);
                  }
                  if (addr.administrativeDistrictLevel1 || addr.state) {
                    cityStateParts.push(
                      addr.administrativeDistrictLevel1 || addr.state
                    );
                  }
                  if (addr.postalCode || addr.zipCode) {
                    cityStateParts.push(addr.postalCode || addr.zipCode);
                  }

                  if (cityStateParts.length > 0) {
                    parts.push(cityStateParts.join(', '));
                  }

                  return parts.length > 0
                    ? parts.join(', ')
                    : 'Address not available';
                })()}
              </span>
            </Box>

            {/* Store Services */}
            {(storeData?.pickup_enabled || storeData?.delivery_enabled) && (
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: '#172B4D',
                    fontFamily: 'Oswald, sans-serif',
                    textTransform: 'uppercase',
                    mb: 1,
                  }}
                >
                  Available Services
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {storeData?.pickup_enabled && (
                    <Box
                      sx={{
                        backgroundColor: '#E8F5E8',
                        color: '#2E7D32',
                        px: 2,
                        py: 0.5,
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      🥡 Pickup
                    </Box>
                  )}
                  {storeData?.delivery_enabled && (
                    <Box
                      sx={{
                        backgroundColor: '#E3F2FD',
                        color: '#1565C0',
                        px: 2,
                        py: 0.5,
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      🚚 Delivery
                    </Box>
                  )}
                  {/* dine-in removed per request */}
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <AboutFoodSection
        foodImage={foodImage}
        sectionTag="About Our Food"
        title="The Best Delicious Food Made From Us..."
        description="Its the perfect dining experience where every dish is crafted with fresh, high-quality dining experience Experience quick and efficient service that ensures your food is servead fresh Its the where every dining experience where every dish is crafted with fresh, high-quality ingredients fresh, high-quality"
        stats={[
          { number: '250+', label: 'Satisfied Clients' },
          { number: '153+', label: 'Total Food Categories' },
          { number: '25+', label: 'Award Win' },
        ]}
        foundationText="Foundation, Since 21st Oct, 2019"
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={'center'}
        variant="section"
        sx={{
          backgroundColor: '#f6f7fc',
          py: { xs: 4, sm: 6, md: 8 },
          mt: { xs: 4, sm: 6, md: 8 },
          minHeight: { xs: '300px', sm: '360px', md: '400px' },
        }}
      >
        <Grid container alignItems="center" spacing={{ xs: 2, sm: 4, md: 6 }}>
          {/* Left: Text and Features */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                maxWidth: '500px',
                mx: 'auto',
                textAlign: { xs: 'center', sm: 'left' },
                px: { xs: 2, sm: 4, md: 6 },
              }}
            >
              <Typography
                sx={{
                  color: environment.mainColor,
                  fontWeight: 600,
                  fontSize: '18px',
                  mb: '10px',
                  letterSpacing: '2px',
                  fontFamily: 'Jost, sans-serif',
                  textTransform: 'uppercase',
                }}
              >
                Best App for Foods Ordering
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '20px', sm: '32px', md: '48px' }, // Adjusted font sizes for better scaling
                  mb: 2,
                  fontFamily: 'Oswald, sans-serif',
                  lineHeight: 1.1,
                }}
              >
                Manage Your Orders
                <br />
                Anytime! Anywhere!
              </Typography>
              <Stack spacing={1} sx={{ mb: { xs: 2, sm: 3 } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <OrangeCircle />
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '18px',
                      color: '#555555',
                    }}
                  >
                    Higher Reach - Minimal Effort
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <OrangeCircle />

                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '18px',
                      color: '#555555',
                    }}
                  >
                    Showcase your Brand
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <OrangeCircle />
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '18px',
                      color: '#555555',
                    }}
                  >
                    Exclusive offers & discounts
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={2}>
                <img
                  src="../../../assets/google-store.png"
                  alt="Google Play"
                  style={{ height: '40px' }}
                />
                <img
                  src="../../../assets/apple-store.png"
                  alt="App Store"
                  style={{ height: '40px' }}
                />
              </Stack>
            </Box>
          </Grid>

          {/* Right: Image */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '424px',
                display: { xs: 'none', md: 'flex' },
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  borderTopLeftRadius: '192px',
                  borderBottomLeftRadius: '192px',
                  overflow: 'hidden',
                  backgroundColor: environment.mainColor,
                  backgroundImage: `url(${chickenBurger})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              ></Box>

              <img
                src="../../../assets/moto.png"
                alt="salad"
                style={{
                  position: 'absolute',
                  bottom: '0px',
                  left: '-7%',
                  objectFit: 'cover',
                  height: '240px',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Stack>

      {/* <Stack
        direction={'row'}
        alignItems={'center'}
        variant="section"
        sx={{
          backgroundColor: 'white',

          mb: 5,
        }}
      >
        <Grid container alignItems="center">
        
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                maxWidth: '500px',
                mx: 'auto',
                textAlign: { xs: 'center', md: 'left' },
                px: 2,
              }}
            >
              <Typography
                sx={{
                  color: environment.mainColor,
                  fontWeight: 600,
                  fontSize: '18px',
                  mb: '10px',
                  letterSpacing: '2px',
                  fontFamily: 'Jost, sans-serif',
                  textTransform: 'uppercase',
                }}
              >
                Frequently Asked Questions
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '18px', md: '28px' },
                  mb: 2,
                  fontFamily: 'Oswald, sans-serif',
                  lineHeight: 1.1,
                }}
              >
                FAQs
              </Typography>
              <Typography
                variant="p"
                sx={{
                  fontSize: '16px',
                  mb: { xs: '16px', md: '24px' },
                  fontFamily: 'Oswald, sans-serif',
                  lineHeight: '33px',
                  color: '#475467',
                  display: 'inline-block',
                }}
              >
                Everything you need to know about us. Can’t find the answer
                you’re looking for? Please get in touch with us for more
                details.
              </Typography>
            </Box>
          </Grid>
  
          <FAQAccordion />
        </Grid>
      </Stack> */}

      {/* Scheduling Dialog - shown for both open and closed branches */}
      <SchedulingDialog
        open={showSchedulingDialog}
        onClose={handleSchedulingClose}
        onConfirm={handleSchedulingConfirm}
        store={storeData}
        orderType={storeData?.delivery_enabled ? 'delivery' : 'pickup'}
        isStoreOpen={storeStatus?.isOpen}
      />
    </Box>
  );
};

export default LocationDetails;
